import React, { useState, useEffect } from 'react';
import { FiSearch, FiBell, FiX, FiGithub, FiFacebook, FiInstagram } from 'react-icons/fi';
import { ref, onValue } from 'firebase/database';
import { database } from '../firebase/config';
import NetflixLogo from './assets/logo.png';

const Header = ({ 
  scrolled, 
  isLoading, 
  toggleSearchModal, 
  setIsDonateModalOpen 
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [viewCount, setViewCount] = useState(0);

  useEffect(() => {
    const countRef = ref(database, 'viewCount/count');
    const unsubscribe = onValue(countRef, (snapshot) => {
      setViewCount(snapshot.val() || 0);
    });

    return () => unsubscribe();
  }, []);

  const toggleNotifications = () => {
    setIsNotificationOpen(!isNotificationOpen);
  };

  return (
    <header className={`fixed top-0 w-full z-50 transition-all duration-500 ${scrolled ? 'bg-black' : 'bg-gradient-to-b from-black to-transparent'} ${isLoading ? 'opacity-0' : 'opacity-100'}`}>
      <div className="flex justify-between items-center px-4 py-3 md:px-12">
        <div className="flex items-center">
          <img 
            src={NetflixLogo} 
            alt="Netflix" 
            className="h-8 md:h-10 mr-4 md:mr-10"
          />
          
          <nav className="hidden md:flex space-x-6 items-center">
            <a href="#" className="text-white hover:text-gray-300">Home</a>
            <div className="flex items-center space-x-2">
              <button 
                onClick={() => {
                  setIsDonateModalOpen(true);
                  setIsMobileMenuOpen(false);
                }} 
                className="text-white hover:text-gray-300"
              >
                Donate
              </button>
              <span className="text-gray-400 text-sm">({viewCount.toLocaleString()} views)</span>
            </div>
          </nav>
        </div>
        
        <div className="md:hidden flex items-center">
          <button onClick={toggleSearchModal} className="text-white hover:text-gray-300 mr-4">
            <FiSearch className="w-5 h-5" />
          </button>
          
          <button 
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="text-white focus:outline-none"
          >
            {isMobileMenuOpen ? (
              <FiX className="w-6 h-6" />
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
        
        <div className="hidden md:flex items-center space-x-4 relative">
          <button onClick={toggleSearchModal} className="text-white hover:text-gray-300">
            <FiSearch className="w-5 h-5" />
          </button>
          
          <div className="relative">
            <button 
              onClick={toggleNotifications}
              className="text-white hover:text-gray-300 relative"
            >
              <FiBell className="w-5 h-5" />
              <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">3</span>
            </button>
            
            {isNotificationOpen && (
              <div className="absolute right-0 mt-2 w-80 bg-black border border-gray-800 rounded-md shadow-lg overflow-hidden z-50">
                <div className="p-4 border-b border-gray-800">
                  <h3 className="text-white font-semibold">Notifications</h3>
                </div>
                
                <div className="max-h-96 overflow-y-auto">
                  <div className="p-4 border-b border-gray-800 hover:bg-gray-900 cursor-pointer">
                    <p className="text-white text-sm">New episode available for your favorite show</p>
                    <p className="text-gray-400 text-xs mt-1">2 hours ago</p>
                  </div>
                  
                  <div className="p-4 border-b border-gray-800 hover:bg-gray-900 cursor-pointer">
                    <p className="text-white text-sm">New movies added to your watchlist</p>
                    <p className="text-gray-400 text-xs mt-1">5 hours ago</p>
                  </div>
                  
                  <div className="p-4 hover:bg-gray-900 cursor-pointer">
                    <p className="text-white text-sm">Your subscription will renew soon</p>
                    <p className="text-gray-400 text-xs mt-1">1 day ago</p>
                  </div>
                </div>
                
                <div className="p-3 bg-gray-900 text-center">
                  <button className="text-white text-sm hover:underline">View All Notifications</button>
                </div>
              </div>
            )}
          </div>
          
          <div className="w-8 h-8 bg-red-600 rounded-md"></div>
        </div>
      </div>
      
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 bg-black bg-opacity-90 z-40 pt-16">
          <div className="flex flex-col h-full px-6 overflow-y-auto">
            {/* Close button at top right */}
            <button 
              onClick={() => setIsMobileMenuOpen(false)}
              className="absolute top-5 right-5 text-white p-2"
            >
              <FiX className="w-6 h-6" />
            </button>
            
            <div className="space-y-4 pb-8">
              <div className="border-b border-gray-800 pb-4">
                <a 
                  href="#" 
                  className="block text-white hover:text-gray-300 text-xl font-medium transition-colors duration-200 py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Home
                </a>
              </div>
              
              <div className="border-b border-gray-800 pb-4">
                <h3 className="text-gray-500 uppercase text-sm mb-2">Categories</h3>
                <a 
                  href="#" 
                  className="block text-gray-400 hover:text-white text-lg transition-colors duration-200 py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  TV Shows
                </a>
                <a 
                  href="#" 
                  className="block text-gray-400 hover:text-white text-lg transition-colors duration-200 py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Movies
                </a>
                <a 
                  href="#" 
                  className="block text-gray-400 hover:text-white text-lg transition-colors duration-200 py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  New & Popular
                </a>
                <a 
                  href="#" 
                  className="block text-gray-400 hover:text-white text-lg transition-colors duration-200 py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  My List
                </a>
              </div>
              
              <div className="border-b border-gray-800 pb-4 text-center">  {/* Added text-center here */}
  <button 
    onClick={() => {
      setIsDonateModalOpen(true);
      setIsMobileMenuOpen(false);
    }} 
    className="text-white hover:text-gray-300 text-lg font-medium transition-colors duration-200 py-2"
  >
    Donate
  </button>
  <p className="text-gray-500 text-sm mt-1">Total views: {viewCount.toLocaleString()}</p>
</div>  
              

            </div>
            
            <div className="pt-6 pb-8 mt-auto">
              <div className="flex justify-center space-x-6">
                <a href="#" className="text-gray-400 hover:text-white">
                  <FiGithub className="w-6 h-6" />
                </a>
                <a href="#" className="text-gray-400 hover:text-white">
                  <FiFacebook className="w-6 h-6" />
                </a>
                <a href="#" className="text-gray-400 hover:text-white">
                  <FiInstagram className="w-6 h-6" />
                </a>
              </div>
              <p className="text-center text-gray-500 text-sm mt-4">© {new Date().getFullYear()} Jericson Lucto</p>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;